import React, { useEffect, useRef, useState } from 'react';
import StepHeader from './StepHeader';
import { ErrorBanner } from '../../lib/ui/Feedback';
import Button from '../../lib/ui/Button';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export default function CashfreePayStep({
  orderResult, te, L, loading, error,
  onPaid, onBack, onRetrySession, onSupportOpen,
}) {
  const [status, setStatus] = useState('ready');
  const [localError, setLocalError] = useState('');
  const pollRef = useRef(null);
  const amount = Number(orderResult?.amount_due ?? orderResult?.total_amount ?? 0);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const verifyOnce = async () => {
    const id = orderResult?.order_id || orderResult?.order_number;
    if (!id || !API) return false;
    try {
      const res = await fetch(`${API}/api/payments/cashfree/verify?order_id=${encodeURIComponent(id)}`, { credentials: 'include' });
      const data = await res.json();
      if (data.paid) {
        if (pollRef.current) clearInterval(pollRef.current);
        onPaid({ ...orderResult, payment_status: 'verified' });
        return true;
      }
    } catch { /* ignore */ }
    return false;
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setStatus('checking');
    pollRef.current = setInterval(() => { verifyOnce(); }, 3000);
    verifyOnce();
  };

  const openCheckout = async () => {
    setLocalError('');
    setStatus('opening');
    try {
      let payUrl = orderResult?.pay_url;
      let sessionId = orderResult?.payment_session_id;
      let env = orderResult?.environment || 'sandbox';

      if (!payUrl && !sessionId && onRetrySession) {
        const refreshed = await onRetrySession();
        if (!refreshed) { setStatus('ready'); return; }
        payUrl = refreshed.pay_url;
        sessionId = refreshed.payment_session_id;
        env = refreshed.environment || env;
      }

      if (payUrl) { window.location.href = payUrl; return; }

      await new Promise((resolve, reject) => {
        if (window.Cashfree) return resolve();
        const s = document.createElement('script');
        s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        s.onload = resolve;
        s.onerror = () => reject(new Error('Failed to load Cashfree SDK'));
        document.head.appendChild(s);
      });
      const cashfree = window.Cashfree({ mode: env === 'production' ? 'production' : 'sandbox' });
      const result = await cashfree.checkout({ paymentSessionId: sessionId, redirectTarget: '_self' });
      if (result?.error) { setLocalError(result.error.message || 'Checkout failed'); setStatus('ready'); return; }
      startPolling();
    } catch (e) {
      setLocalError(e.message || 'Could not open payment');
      setStatus('ready');
    }
  };

  useEffect(() => {
    if (orderResult?.payment_session_id || orderResult?.pay_url) openCheckout();
  }, []);

  return (
    <div>
      <StepHeader title={`💳 ${L('Pay Online', 'ఆన్‌లైన్ చెల్లింపు')}`} onClose={onBack} />

      <div className="bg-brand-light/50 rounded-2xl px-4 py-3.5 mb-4">
        <div className="font-black text-brand-dark text-[15px]">{L('Order', 'ఆర్డర్')} #{orderResult?.order_number || '…'}</div>
        <div className="mt-1.5 font-extrabold text-2xl text-sky-700">₹{amount.toFixed(2)}</div>
        <div className="mt-1.5 text-xs text-ink-soft font-bold">{L('UPI · Cards · Netbanking via Cashfree', 'Cashfree ద్వారా UPI · కార్డులు · నెట్‌బ్యాంకింగ్')}</div>
      </div>

      <ErrorBanner>{error || localError}</ErrorBanner>

      <Button variant="blue" fullWidth loading={loading || status === 'opening'} onClick={openCheckout}>
        {status === 'opening' ? L('Opening Cashfree…', 'Cashfree తెరుస్తున్నాం…') : L('Pay securely with Cashfree', 'Cashfree తో సురక్షితంగా చెల్లించండి')}
      </Button>
      <Button variant="danger" fullWidth className="mt-2.5" onClick={startPolling}>
        {status === 'checking' ? L('Checking payment…', 'చెల్లింపు తనిఖీ…') : L("I've paid — verify", 'చెల్లించాను — నిర్ధారించు')}
      </Button>
      <Button variant="ghost" fullWidth className="mt-2" onClick={onBack}>← {L('Back', 'వెనక్కి')}</Button>

      <div className="flex items-center justify-center gap-3.5 mt-3.5 pt-3 border-t border-black/5">
        <span className="text-ink-faint text-xs">{L('Need help?', 'సహాయం కావాలా?')}</span>
        <button type="button" onClick={onSupportOpen} className="bg-gold-light text-gold rounded-full px-4 py-1.5 font-extrabold text-xs">🆘 {L('Contact Support', 'సపోర్ట్ సంప్రదించు')}</button>
      </div>
    </div>
  );
}
